import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { useAuth } from '../hooks/useAuth';
import { auth } from '../firebase';
import InvoiceTable from './InvoiceTable';
import Sidebar from './Sidebar';
import { Invoice, Team, User } from '../types';
import { getInvoices, getUserTeams, subscribeToInvoices, getUserData } from '../services/firebaseService';
import './HomePage.css';

const HomePage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userData, setUserData] = useState<User | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [unsubscribeInvoices, setUnsubscribeInvoices] = useState<(() => void) | null>(null);

  const loadUserData = async () => {
    if (user) {
      try {
        const data = await getUserData(user.uid);
        console.log('User data loaded:', data);
        setUserData(data);
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    }
  };

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDateRange();
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeInvoices) {
        unsubscribeInvoices();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Cleanup previous subscription
    if (unsubscribeInvoices) {
      unsubscribeInvoices();
      setUnsubscribeInvoices(null);
    }

    if (selectedTeam && dateRange.startDate && dateRange.endDate) {
      setLoading(true);
      
      // Set up real-time subscription
      const unsubscribe = subscribeToInvoices(
        selectedTeam,
        dateRange.startDate,
        dateRange.endDate,
        (invoiceData) => {
          setInvoices(invoiceData);
          setLoading(false);
        }
      );
      
      setUnsubscribeInvoices(() => unsubscribe);
    }
    
    // Cleanup on unmount
    return () => {
      if (unsubscribeInvoices) {
        unsubscribeInvoices();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam, dateRange]);

  const setInitialDateRange = () => {
    const currentDay = new Date().getDate();
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);

    let startDate, endDate;
    
    if (currentDay < 26) {
      startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-26`;
      endDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-25`;
    } else {
      startDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-26`;
      endDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}-25`;
    }

    setDateRange({ startDate, endDate });
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const invoiceData = await getInvoices(selectedTeam, dateRange.startDate, dateRange.endDate);
      setInvoices(invoiceData);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamSelect = (teamId: string) => {
    setSelectedTeam(teamId);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleDateRangeChange = (newDateRange: { startDate: string; endDate: string }) => {
    setDateRange(newDateRange);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Erro ao fazer logout.');
    }
  };

  if (!selectedTeam) {
    return (
      <div className="home-page">
        <Sidebar 
          teams={teams}
          selectedTeam={selectedTeam}
          onTeamSelect={handleTeamSelect}
          userRole={userData?.type || 'team'}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={toggleSidebar}
        />
        <div className={`home-content ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
          <div className="home-header">
            <button onClick={toggleSidebar} className="sidebar-toggle-btn">
              <i className={`fas ${sidebarOpen ? 'fa-bars' : 'fa-bars'}`}></i>
            </button>
            <div className="user-info">
              <span className="user-email">{user?.email}</span>
              {isAdmin && <span className="admin-badge">Admin</span>}
              <button onClick={handleLogout} className="logout-btn">
                Sair
              </button>
            </div>
          </div>
          
          <div className="welcome-section">
            <div className="welcome-content">
              <h1>Sistema de Faturamento</h1>
              <p>Escolha uma equipe no menu ao lado para começar.</p>
              <div className="welcome-stats">
                <div className="stat-card">
                  <h3>{teams.length}</h3>
                  <p>Equipes Disponíveis</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <Sidebar 
        teams={teams}
        selectedTeam={selectedTeam}
        onTeamSelect={handleTeamSelect}
        userRole={userData?.type || 'team'}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={toggleSidebar}
      />
      <div className={`home-content ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
        <div className="home-header">
          <button onClick={toggleSidebar} className="sidebar-toggle-btn">
            <i className={`fas ${sidebarOpen ? 'fa-bars' : 'fa-bars'}`}></i>
          </button>
          <div className="user-info">
            <span className="user-email">{user?.email}</span>
            {isAdmin && <span className="admin-badge">Admin</span>}
            <button onClick={handleLogout} className="logout-btn">
              Sair
            </button>
          </div>
        </div>
        
        <InvoiceTable
          invoices={invoices}
          selectedTeam={selectedTeam}
          loading={loading}
          userRole={userData?.type || (isAdmin ? 'admin' : 'team')}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          onRefresh={loadInvoices}
          userData={userData || undefined}
        />
      </div>
    </div>
  );
};

export default HomePage;